// src/components/TIMEBAR/GenericRobotEvents.js
import React, { useEffect, useRef, useCallback } from "react";
import { styles } from "./parseEvents"; 
import { getParsedEvents } from "../../store/eventCache";
import EventIcon from "./ EventIcons";
import { useAppStore } from "../../store/appStore";

// Internal tooltip management
let tooltipElement = null;
let tooltipTimeout = null;

const createTooltip = () => {
  if (!tooltipElement) {
    tooltipElement = document.createElement('div');
    tooltipElement.className = 'timeline-event-tooltip';
    tooltipElement.style.cssText = `
      position: fixed;
      background-color: white;
      padding: 8px;
      border-radius: 4px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      border: 1px solid #e5e7eb;
      font-size: 12px;
      white-space: nowrap;
      pointer-events: none;
      z-index: 10000;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.15s ease;
    `;
    document.body.appendChild(tooltipElement);
  }
  return tooltipElement;
};

const showTooltip = (content, x, y) => {
  const tooltip = createTooltip();
  
  // Clear any previous timeout
  if (tooltipTimeout) {
    clearTimeout(tooltipTimeout);
    tooltipTimeout = null;
  }
  
  tooltip.innerHTML = content;
  tooltip.style.left = `${x}px`;
  tooltip.style.top = `${y}px`;
  tooltip.style.opacity = '1';
  tooltip.style.visibility = 'visible';
};

const hideTooltip = () => {
  if (tooltipElement) {
    tooltipElement.style.opacity = '0';
    
    // Use a delay to prevent flickering
    tooltipTimeout = setTimeout(() => {
      if (tooltipElement) {
        tooltipElement.style.visibility = 'hidden';
      }
    }, 150);
  }
};

const GenericRobotEvents = ({ robotId, onEventClick, maxTime, timeframe = "11AM", activeEventId }) => {
  const events = getParsedEvents(robotId, timeframe);
  const activeEventRef = useRef({});
  const isProcessingClickRef = useRef(false);
  
  // Get Zustand state and actions directly to ensure proper updates
  const metadata = useAppStore(state => state.metadata);
  const updateVideoState = useAppStore(state => state.updateVideoState);
  
  // Clean up tooltip on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeout) {
        clearTimeout(tooltipTimeout);
        tooltipTimeout = null;
      }
    };
  }, []);
  
  // Format time display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Monitor processing status to reset click lock
  useEffect(() => {
    if (!metadata.processingUpdate) {
      isProcessingClickRef.current = false;
    }
  }, [metadata.processingUpdate]);

  // GenericRobotEvents.js 中的 handleEventClick 函数修改
  const handleEventClick = useCallback((event, e, eventIndex) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
      
      // 防止快速点击导致的重复处理
      if (isProcessingClickRef.current) {
        console.log(`[Events] ⏭️ 忽略重复的事件点击，上一个处理仍在进行中`);
        return;
      }
      
      // 设置处理锁
      isProcessingClickRef.current = true;
      
      console.log(`[Events] 📍 事件点击: robotId=${robotId}, eventIndex=${eventIndex}, startTime=${event.startTime}, endTime=${event.endTime}`);
      
      // 立即更新视觉高亮效果
      try {
        // 更新时间轴上的高亮
        document.querySelectorAll('.timeline-event').forEach(el => {
          el.classList.remove('active');
        });
        
        const eventElement = document.getElementById(`timeline-event-robot${robotId}-${eventIndex}`);
        if (eventElement) {
          eventElement.classList.add('active');
        }
        
        // 新增代码：同时高亮对应的故事章节
        const storyChapter = document.querySelector(`[data-event-id="${eventIndex}"]`);
        if (storyChapter) {
          // 添加视觉高亮
          storyChapter.classList.add('story-highlight');
          
          // 滚动到该章节
          storyChapter.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          
          // 动画结束后移除高亮
          setTimeout(() => {
            storyChapter.classList.remove('story-highlight');
          }, 2000);
        }
      } catch (err) {
        console.warn("[Events] ⚠️ 更新事件视觉效果时出错:", err);
      }
      
      // 根据时间框架生成URL
      const timePart = timeframe === "11AM" ? "day" : "night";
      const videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
      
      console.log(`[Events] 🎬 准备跳转到视频时间点: ${event.startTime}秒`);
      
      // 更新视频状态，直接跳转到指定时间
      try {
        updateVideoState({
          robotId,
          eventId: eventIndex,
          videoUrl,
          time: event.startTime,
          isPlaying: true,
          isHls: false,
          hlsResumeTime: null,
          hlsBaseTime: 0,
          currentEvent: event
        }, 'timeline_event_click');
        
        console.log(`[Events] ✅ 视频状态已更新，跳转到时间点: ${event.startTime}秒`);
      } catch (err) {
        console.error(`[Events] ❌ 更新视频状态时出错:`, err);
      }
  
      // 短暂延迟后重置处理锁
      setTimeout(() => {
        isProcessingClickRef.current = false;
      }, 100);
      
      // 可选：调用原有的事件处理函数
      if (onEventClick) {
        onEventClick(event.startTime, robotId, eventIndex);
      }
    }
  }, [robotId, onEventClick, updateVideoState, timeframe]);
  // Mouse enter handler
  const handleMouseEnter = (event, domEvent) => {
    // Save reference to current active event
    activeEventRef.current = { 
      element: domEvent.currentTarget,
      event: event
    };
    
    // Set hover color for current event
    const innerElement = domEvent.currentTarget.querySelector('.event-inner');
    if (innerElement) {
      innerElement.style.backgroundColor = event.hoverColor;
    }
    
    // Get position of event element relative to viewport
    const rect = domEvent.currentTarget.getBoundingClientRect();
    
    // Calculate tooltip position (centered above element)
    const x = rect.left + (rect.width / 2);
    const y = rect.top - 10;
    
    // Prepare tooltip content
    const content = `
      <div style="font-weight: bold">${event.event}</div>
      <div style="color: #666">
        ${formatTime(event.startTime)} - ${formatTime(event.endTime)}
      </div>
    `;
    
    // Show tooltip
    showTooltip(content, x, y);
  };
  
  // Mouse leave handler
  const handleMouseLeave = (event, domEvent) => {
    // If currently leaving the active event, reset active event reference
    if (activeEventRef.current.element === domEvent.currentTarget) {
      // Restore original color
      const innerElement = domEvent.currentTarget.querySelector('.event-inner');
      if (innerElement) {
        innerElement.style.backgroundColor = event.color;
      }
      
      // Hide tooltip
      hideTooltip();
      
      // Clear active event reference
      activeEventRef.current = {};
    }
  };

  return (
    <div style={styles.container}>
      {events.map((event, index) => (
        <div
          key={index}
          id={`timeline-event-robot${robotId}-${index}`}
          className={`timeline-event ${activeEventId === index ? 'active' : ''}`}
          style={{
            ...styles.eventMarker,
            left: `${event.startPosition * 100}%`,
            width: `${(event.endPosition - event.startPosition) * 100}%`,
          }}
          onMouseEnter={(e) => handleMouseEnter(event, e)}
          onMouseLeave={(e) => handleMouseLeave(event, e)}
          onClick={(e) => handleEventClick(event, e, index)}
          data-event-id={index}
          data-robot-id={robotId}
          data-start-time={event.startTime}
        >
          <div
            className="event-inner"
            style={{
              ...styles.eventInner,
              backgroundColor: event.color,
            }}
          />
          <EventIcon type={event.type} />
        </div>
      ))}
    </div>
  );
};

export default React.memo(GenericRobotEvents);